import {
  AvailableDataColumn,
  ColumnRelationship,
  ColumnType,
  DataColumn,
  DataResults,
  DataRow,
  DataRows,
  FieldWarning,
  DataWarning,
  GroupByResultType,
  IdentifierColumn,
  KpiWithChangeColumn,
  ResultSet,
  TimeFrame,
  DataWarningDetails,
  DataWarningKpi,
  GenericWarningScope,
} from '../types';

export class DataStore {
  private groupedResults: ResultSet[];

  constructor(
    private readonly data: DataResults,
    readonly stamp: number
  ) {}

  hasDataResultSetsValues(): boolean {
    return this.getGroupedSets().some(dataset => this.hasValidData(dataset));
  }

  private hasValidData(dataset: { data?: Array<{ values: Record<string, unknown> }> }): boolean {
    if (!dataset.data || dataset.data.length === 0) {
      return false;
    }

    return dataset.data.some(row => this.hasNonEmptyValues(row.values));
  }

  private hasNonEmptyValues(values: Record<string, unknown>): boolean {
    return Object.values(values).some(rowValue => {
      if (Array.isArray(rowValue)) {
        return rowValue.some(v => !!v);
      }
      return !!rowValue;
    });
  }

  getGroupedSets(): ResultSet[] {
    if (!this.groupedResults) {
      this.groupedResults = this.data?.results?.filter(set => set.type.type === 'group_by') || [];
    }
    return this.groupedResults;
  }

  hasDataInSomeGroupedSets(): boolean {
    return this.getGroupedSets().some(gs => gs.data.some(el => el.hasData));
  }

  getGroupedData(groupings: string[], exact: boolean = false): { data: DataRows; timeFrame: TimeFrame }[] {
    groupings = groupings.filter(group => group.length > 0) ?? [];

    if (!exact) {
      groupings.sort((a, b) => a.localeCompare(b));
    }
    const match = JSON.stringify(groupings);
    const matches =
      this.getGroupedSets().filter(set => {
        const groupBys = [...(set.type as GroupByResultType).groupBy];
        if (!exact) {
          groupBys.sort((a, b) => a.localeCompare(b));
        }
        return JSON.stringify(groupBys) === match;
      }) ?? [];
    return matches.map(set => ({
      data: set.data,
      timeFrame: set.type.timeFrame,
    }));
  }

  hasWarnings(): boolean {
    // check every set for warnings
    return this.data.warnings.length > 0 || this.getGroupedSets().some(set => set.warnings?.length > 0);
  }

  getGenericWarnings() {
    return this.data.warnings.filter(warning => warning.scope !== GenericWarningScope.Global);
  }

  getDataWarnings(selectedKpis: string[]): DataWarning[] {
    const dataWarnings = new Map<string, DataWarningDetails>();
    this.getGroupedSets().forEach(set => {
      set.warnings?.forEach(warning => {
        const kpiInfo = this.getKpiInfoWithWarnings(warning, selectedKpis, set.columns);

        if (!kpiInfo.length) {
          return;
        }
        dataWarnings.set(warning.message, {
          level: warning.level,
          kpis: kpiInfo,
        });
      });
    });

    return Array.from(dataWarnings);
  }

  private getKpiInfoWithWarnings(
    warning: FieldWarning,
    selectedKpis: string[],
    columns: DataColumn[]
  ): DataWarningKpi[] {
    const relatedFields = selectedKpis.filter(kpi => warning.relatedFields?.includes(kpi));
    if (!relatedFields.length) {
      return [];
    }

    const relatedFieldNames = relatedFields.map(field => {
      const { label, name } = columns.find(col => col.name === field) ?? { label: '', name: '' };

      return {
        label,
        name,
      };
    });

    return relatedFieldNames;
  }

  getDataColumns(): DataColumn[] {
    const groupedSets = this.getGroupedSets();
    const groupSetWithColumns = groupedSets.find(set => set.columns);
    return groupSetWithColumns?.columns || [];
  }

  getUpsellKpisIndicator(): boolean {
    return this.getGroupedSets()[0]?.upsellKpisIndicator || false;
  }

  getDataColumn(withName: string): DataColumn {
    let column = this.getDataColumns().find(col => col.name === withName);
    if (column) {
      return column;
    }
    column = this.getDataColumns().find(col => (col as KpiWithChangeColumn)?.changeName === withName);
    if (column) {
      const withChangeCol = column as KpiWithChangeColumn;
      return {
        name: withChangeCol.changeName,
        label: withChangeCol.changeLabel,
        arrayLength: withChangeCol.arrayLength,
        belongsTo: withChangeCol.belongsTo,
        canAggregate: withChangeCol.canAggregate,
        type: ColumnType.Kpi,
        isChange: true,
        dataType: withChangeCol.changeDataType,
        formatHint: withChangeCol.changeFormatHint,
        relatedTo: withChangeCol.name,
        relationship: ColumnRelationship.Change,
      } as DataColumn;
    }
  }

  getAvailableColumns(): AvailableDataColumn[] {
    const availableColumns = this.getGroupedSets()[0]?.availableColumns;
    if (availableColumns) {
      return availableColumns?.flatMap(avCols =>
        avCols.columns.map(col => ({
          ...col,
          enrichment: avCols.enrichment,
        }))
      );
    }
    return [];
  }

  getAllColumns(): DataColumn[] {
    return [...this.getDataColumns(), ...this.getAvailableColumns()];
  }

  getCurrency(): string | undefined {
    const grouped = this.getGroupedSets()[0];
    if (!grouped) {
      return;
    }
    return (grouped.type as GroupByResultType).currency;
  }

  static getCellsLatestValue(row: DataRow, columnName: string): number | null {
    const value = row.values[columnName];
    let actualValue: number | null = null;

    if (typeof value === 'number') {
      actualValue = value;
    } else if (Array.isArray(value)) {
      actualValue = value[value.length - 1];
    }

    return actualValue;
  }

  static getRowIdentifiers(
    row: DataRow,
    columns: DataColumn[],
    grouping: string
  ): { id: string; name: string; description: string } | undefined {
    let identifierColumn = columns.find(col => col.name === grouping && col.type === ColumnType.Identifier);

    if (!identifierColumn) {
      const column = columns.find(col => col.name === grouping);
      if (column) {
        const descriptorColumn = columns.find(
          col => col.relatedTo === column.name && col.relationship === ColumnRelationship.Description
        );
        return {
          id: String(row.values[column.name]),
          name: String(row.values[descriptorColumn?.name || column.name]),
          description: '',
        };
      }
    }

    if (!identifierColumn) {
      identifierColumn = columns.find(col => col.type === ColumnType.Identifier);
    }

    if (identifierColumn) {
      const { identifier, subIdentifier, idField } = identifierColumn as IdentifierColumn;
      let idTexts: string[] = [];
      identifier.forEach(field => {
        const value = row.values[field];
        if (value) {
          idTexts.push(String(value));
        }
      });

      if (idTexts.length === 0) {
        idTexts.push(String(row.values[idField]));
      }

      let subTexts: string[] = [];
      if (subIdentifier) {
        subIdentifier.forEach(field => {
          const value = row.values[field];
          if (value) {
            subTexts.push(String(value));
          }
        });
      }

      return {
        id: String(row.values[idField]),
        name: idTexts.join(' ').trim(),
        description: subTexts.join(' ').trim(),
      };
    }
  }
}
